import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import type {
    ListShippingCarriersParams,
    UpsertShippingCarrierRequest,
} from '@/lib/api/shipping-carriers-api'
import { shippingCarriersApi } from '@/lib/api/shipping-carriers-api'
import { ApiError } from '@/lib/api/client'
import { m } from '@/lib/paraglide/messages'

export const shippingCarrierKeys = {
    all: ['shipping-carriers'] as const,
    lists: () => [...shippingCarrierKeys.all, 'list'] as const,
    list: (params: ListShippingCarriersParams) =>
        [...shippingCarrierKeys.lists(), params] as const,
    detail: (id: string) => [...shippingCarrierKeys.all, 'detail', id] as const,
}

export function useShippingCarriers(
    params?: ListShippingCarriersParams,
    options: { enabled?: boolean } = {},
) {
    return useQuery({
        enabled: options.enabled,
        queryKey: shippingCarrierKeys.list(params ?? {}),
        queryFn: async () => {
            const res = await shippingCarriersApi.list(params)
            return { data: res.data ?? [], meta: res.meta }
        },
    })
}

export function useShippingCarrier(id: string) {
    return useQuery({
        queryKey: shippingCarrierKeys.detail(id),
        queryFn: async () => {
            const res = await shippingCarriersApi.getById(id)
            return res.data ?? null
        },
        enabled: Boolean(id),
    })
}

function handleShippingCarrierMutationError(err: unknown) {
    if (err instanceof ApiError && err.status === 409) {
        toast.error(m.toast_shipping_carrier_duplicate_name())
        return
    }
    toast.error(m.toast_shipping_carrier_save_error())
}

export function useCreateShippingCarrier() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (data: UpsertShippingCarrierRequest) =>
            shippingCarriersApi.create(data),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: shippingCarrierKeys.lists() })
            toast.success(m.toast_shipping_carrier_save_success())
        },
        onError: handleShippingCarrierMutationError,
    })
}

export function useUpdateShippingCarrier(id: string) {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (data: UpsertShippingCarrierRequest) =>
            shippingCarriersApi.update(id, data),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: shippingCarrierKeys.lists() })
            void qc.invalidateQueries({
                queryKey: shippingCarrierKeys.detail(id),
            })
            toast.success(m.toast_shipping_carrier_save_success())
        },
        onError: handleShippingCarrierMutationError,
    })
}

export function useDeleteShippingCarrier() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (id: string) => shippingCarriersApi.delete(id),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: shippingCarrierKeys.lists() })
            toast.success(m.toast_shipping_carrier_delete_success())
        },
        onError: () => toast.error(m.toast_shipping_carrier_delete_error()),
    })
}
